import type React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, useUser, SignInButton, useClerk } from '@clerk/clerk-react';
import { Button } from '@/components/ui/button';
import { useUserRole } from '@/hooks/useUserRole';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ChevronDown, Settings, FileText, LogOut, User } from 'lucide-react';
import { getVisibleNavItems, isActiveRoute as checkActiveRoute } from './appNavigation';

/**
 * AppHeader: Modern fixed header with landing page aesthetic
 *
 * Features:
 * - Fixed positioning with backdrop blur glassmorphism
 * - Serif two-tone logo (Vantage + Draft in terracotta)
 * - Three-section layout: logo, navigation, auth
 * - Admin role detection for conditional navigation
 * - Responsive design (mobile-friendly)
 * - Custom auth UI replacing Clerk UserButton
 */
export const AppHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSignedIn } = useAuth();
  const { user } = useUser();
  const { isAdmin } = useUserRole();
  const { signOut } = useClerk();

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Get visible navigation items based on user role
  const visibleNavItems = getVisibleNavItems(isAdmin);

  // Active route check
  const isActiveRoute = (path: string) => {
    return checkActiveRoute(path, location.pathname);
  };

  const displayName = user?.firstName || user?.emailAddresses[0]?.emailAddress?.split('@')[0] || 'User';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b border-charcoal/5 bg-paper/80">
      <div className="max-w-7xl mx-auto px-4 h-[var(--app-header-height)] flex items-center justify-between">
        {/* Logo */}
        <button
          type="button"
          onClick={() => navigate(isSignedIn ? '/dashboard' : '/')}
          className="font-serif font-bold text-lg tracking-tighter text-charcoal hover:opacity-80 transition-opacity"
        >
          Vantage<span className="text-terracotta">Draft</span>
        </button>

        {/* Center Navigation (only when signed in, xl breakpoint only) */}
        {isSignedIn && (
          <div className="hidden xl:flex items-center gap-6">
            {visibleNavItems.map((item) => (
              <button
                type="button"
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`text-sm font-medium transition-colors ${
                  isActiveRoute(item.path)
                    ? 'text-terracotta'
                    : 'text-charcoal/80 hover:text-terracotta'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}

        {/* Auth Section */}
        <div className="flex items-center gap-3">
          {isSignedIn ? (
            <>
              {/* User Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button type="button" className="flex items-center gap-2 text-sm font-medium text-charcoal/80 hover:text-charcoal transition-colors">
                    <span className="hidden sm:inline">{displayName}</span>
                    <User className="w-4 h-4 sm:hidden" />
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    <div className="font-medium text-foreground">{displayName}</div>
                    <div className="text-xs truncate">
                      {user?.emailAddresses[0]?.emailAddress}
                    </div>
                  </div>
                  <DropdownMenuSeparator />

                  {/* Mobile Navigation (shown only on small screens) */}
                  <div className="md:hidden">
                    <DropdownMenuItem onSelect={() => navigate('/dashboard')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Articles
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate('/inspiration')}>
                      <FileText className="w-4 h-4 mr-2" />
                      Inspiration
                    </DropdownMenuItem>
                     {isAdmin && (
                      <>
                        <DropdownMenuItem onSelect={() => navigate('/outline-templates')}>
                          <FileText className="w-4 h-4 mr-2" />
                          Outline Templates
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate('/style-guides')}>
                          <FileText className="w-4 h-4 mr-2" />
                          Writing Styles
                        </DropdownMenuItem>
                        <DropdownMenuItem onSelect={() => navigate('/settings/config')}>
                          <Settings className="w-4 h-4 mr-2" />
                          Settings
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                  </div>

                  <DropdownMenuItem onSelect={handleSignOut}>
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              {/* Signed Out - Landing Page Style Buttons */}
              <SignInButton mode="modal">
                <button type="button" className="text-sm font-medium text-charcoal hover:text-terracotta transition-colors hidden sm:block">
                  Log in
                </button>
              </SignInButton>
              <SignInButton mode="modal">
                <Button
                  size="sm"
                  className="bg-charcoal text-white hover:bg-black text-xs font-bold px-4 py-1.5 rounded-md shadow-sm"
                >
                  Get Started
                </Button>
              </SignInButton>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};
